import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  CircleHelp,
  FileSignature,
  GraduationCap,
  MessagesSquare,
  PenLine,
  Target,
  UsersRound,
} from "lucide-react";

import BottomNav from "../components/BottomNav";
import SignatureModal from "../components/SignatureModal";
import AppraisalJourney from "../components/appraisal/AppraisalJourney";
import RatingScale from "../components/appraisal/RatingScale";
import JobDescriptionView from "../components/jd/JobDescriptionView";
import { useTabParam } from "../hooks/useTabParam";
import {
  MIN_EXAMPLE_CHARS,
  formatDate,
  hasExample,
  needsExample,
  ratingScale,
  reflectionScale,
  selfAssessmentSteps,
} from "../utils/appraisal";
import type { JDContent } from "../utils/jobDescription";

/* ──────────────────────────────────────────────────────────────────────────
 * Help & training: an interactive demo area for showing people how
 * appraisals and job descriptions work. Everything here is made up
 * (a fictional colleague, Sam Taylor) and nothing is saved — it exists
 * so a trainer can click through the real controls in front of a room.
 * ────────────────────────────────────────────────────────────────────────── */

const TABS = ["appraisal", "jd", "scales"] as const;
type Tab = (typeof TABS)[number];

const STAGE_STATUS = [
  "self_assessment_in_progress",
  "colleague_feedback_in_progress",
  "manager_review_in_progress",
  "meeting_pending",
  "completed",
] as const;

/** The fictional job description the demo signs. */
function demoJd(t: (k: string, fb: string) => string): JDContent {
  return {
    details: {
      name: "Sam Taylor",
      job_title: t("help.jd.demoTitle", "Payroll Administrator"),
      department: t("help.jd.demoDept", "Shared Services"),
      reports_to: "Thandi Nkosi",
      direct_reports: t("help.jd.demoNone", "None"),
      qualifications: t("help.jd.demoQuals", "Payroll certificate (or working towards one)"),
      scope: "PME Shared Services",
      hr_contact: t("help.jd.demoHr", "The HR team"),
      date_last_reviewed: "",
    },
    purpose: t(
      "help.jd.demoPurpose",
      "Run the monthly payroll accurately and on time, keep employee records right, and be the first point of help for pay questions across PME Shared Services.",
    ),
    kras: [
      {
        area: t("help.jd.kra1", "Payroll processing"),
        weighting: 40,
        covers: t("help.jd.kra1covers", "Preparing, checking and running the monthly payroll for all Shared Services staff."),
        measured_by: "",
        tasks: [
          t("help.jd.kra1t1", "Collect and check timesheets, leave and changes before each run."),
          t("help.jd.kra1t2", "Run the payroll to the agreed timetable."),
          t("help.jd.kra1t3", "Resolve any differences before payday."),
        ],
        measures: [
          { text: t("help.jd.kra1m1", "Payroll paid on time every month"), proposed: false },
          { text: t("help.jd.kra1m2", "Fewer than 2 corrections per run"), proposed: false },
        ],
      },
      {
        area: t("help.jd.kra2", "Employee records and data quality"),
        weighting: 25,
        covers: t("help.jd.kra2covers", "Keeping starter, leaver and change records accurate in PME People."),
        measured_by: "",
        tasks: [
          t("help.jd.kra2t1", "Enter starters, leavers and changes within 2 working days."),
          t("help.jd.kra2t2", "Run a monthly data check and fix what it finds."),
        ],
        measures: [{ text: t("help.jd.kra2m1", "Monthly data check passed"), proposed: false }],
      },
      {
        area: t("help.jd.kra3", "Queries and support"),
        weighting: 20,
        covers: t("help.jd.kra3covers", "Answering pay and benefits questions from colleagues, kindly and clearly."),
        measured_by: "",
        tasks: [t("help.jd.kra3t1", "Answer pay queries within 2 working days."), t("help.jd.kra3t2", "Keep a simple log of common questions.")],
        measures: [{ text: t("help.jd.kra3m1", "Queries answered within 2 working days"), proposed: false }],
      },
      {
        area: t("help.jd.kra4", "Reporting and compliance"),
        weighting: 15,
        covers: t("help.jd.kra4covers", "Monthly payroll reports and statutory filings, done right and on time."),
        measured_by: "",
        tasks: [t("help.jd.kra4t1", "Send the monthly payroll summary to Finance."), t("help.jd.kra4t2", "File statutory returns by their deadlines.")],
        measures: [{ text: t("help.jd.kra4m1", "A quarterly accuracy score, to be agreed"), proposed: true }],
      },
    ],
    communication: {
      covers: t("help.jd.commCovers", "Replying to colleagues promptly, flagging problems early, and keeping your manager informed."),
      measured_by: t("help.jd.commMeasured", "Feedback from colleagues and your manager through the year."),
    },
    competencies: [
      { name: t("help.jd.comp1", "Accuracy"), description: t("help.jd.comp1d", "Gets the details right first time; checks their own work.") },
      { name: t("help.jd.comp2", "Helpfulness"), description: t("help.jd.comp2d", "Treats every question as worth answering well.") },
      { name: t("help.jd.comp3", "Confidentiality"), description: t("help.jd.comp3d", "Handles pay and personal data with care, always.") },
    ],
    proposed_note: t("help.jd.proposedNote", "To be agreed with your manager in your first review."),
  };
}

export default function HelpPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useTabParam<Tab>(TABS, "appraisal");

  const tabs: { key: Tab; label: string }[] = [
    { key: "appraisal", label: t("help.tab.appraisal", "How an appraisal works") },
    { key: "jd", label: t("help.tab.jd", "Job descriptions") },
    { key: "scales", label: t("help.tab.scales", "The rating scales") },
  ];

  return (
    <div className="flex min-h-dvh flex-col bg-gray-50 safe-x">
      <header className="safe-top bg-white px-5 pt-4 pb-3 shadow-sm">
        <div className="mx-auto w-full max-w-3xl">
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
            <GraduationCap size={24} className="text-pme-blue" aria-hidden />
            {t("help.title", "Help & training")}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {t("help.subtitle", "Click through how appraisals and job descriptions work — made for showing a room, or finding your own feet.")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {tabs.map((x) => (
              <button
                key={x.key}
                type="button"
                onClick={() => setTab(x.key)}
                aria-pressed={tab === x.key}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  tab === x.key ? "bg-pme-navy text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {x.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-5 pb-24 pt-4">
        <div className="mx-auto w-full max-w-3xl space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            <p className="font-semibold">{t("help.demoBanner", "This is a training area.")}</p>
            <p className="mt-0.5">
              {t("help.demoBannerBody", "Sam Taylor and every answer here are made up. Click anything — nothing is saved and nobody is emailed.")}
            </p>
          </div>

          {tab === "appraisal" && <AppraisalDemo />}
          {tab === "jd" && <JdDemo />}
          {tab === "scales" && <ScalesReference />}
        </div>
      </main>
      <BottomNav />
    </div>
  );
}

/* ── The appraisal, stage by stage ───────────────────────────────────────── */

function AppraisalDemo() {
  const { t } = useTranslation();
  const [stage, setStage] = useState(0);

  const stageTabs = [
    { label: t("help.appr.s1", "1 · Self-assessment"), icon: PenLine },
    { label: t("help.appr.s2", "2 · Colleague feedback"), icon: MessagesSquare },
    { label: t("help.appr.s3", "3 · Manager's review"), icon: UsersRound },
    { label: t("help.appr.s4", "4 · The meeting"), icon: Target },
    { label: t("help.appr.s5", "5 · The outcome"), icon: CheckCircle2 },
  ];

  return (
    <>
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-base font-bold text-pme-navy">{t("help.appr.journeyTitle", "The journey")}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t("help.appr.journeyIntro", "Every appraisal moves through the same five stages. Click a stage below and the journey updates, exactly as it does for a real appraisal.")}
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {stageTabs.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStage(i)}
              aria-pressed={stage === i}
              className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                stage === i ? "border-pme-blue bg-pme-blue text-white" : "border-gray-200 bg-white text-gray-600 hover:border-pme-blue/40"
              }`}
            >
              <s.icon size={13} aria-hidden />
              {s.label}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <AppraisalJourney status={STAGE_STATUS[stage]} managerName="Thandi" />
        </div>
      </section>

      {stage === 0 && <SelfStageDemo />}
      {stage === 1 && <PeersStageDemo />}
      {stage === 2 && <ManagerStageDemo />}
      {stage === 3 && <MeetingStageDemo />}
      {stage === 4 && <OutcomeStageDemo />}
    </>
  );
}

function DemoCard({ title, intro, children }: { title: string; intro: string; children?: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="text-base font-bold text-pme-navy">{title}</h2>
      <p className="mt-1 text-sm text-gray-600">{intro}</p>
      {children}
    </section>
  );
}

function TryIt({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-xl border border-dashed border-pme-blue/40 bg-pme-sky/30 p-3">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-pme-blue">{t("help.tryIt", "Try it")}</p>
      {children}
    </div>
  );
}

function SelfStageDemo() {
  const { t } = useTranslation();
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState("");
  const steps = selfAssessmentSteps();

  return (
    <DemoCard
      title={t("help.self.title", "Stage 1 — Your self-assessment")}
      intro={t("help.self.intro", "The person looks back on their year in 9 short steps. It takes 30 to 45 minutes, saves as they go, and they can stop and come back. They rate themselves in words, not numbers — it's a reflection, not a score.")}
    >
      <ol className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-3">
        {steps.map((s) => (
          <li key={s.n} className="rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-1.5" title={s.intro}>
            <span className="text-xs font-bold text-pme-blue">{s.n}</span>
            <span className="ml-1.5 text-xs font-semibold text-gray-700">{s.short}</span>
          </li>
        ))}
      </ol>
      <TryIt>
        <p className="text-sm font-semibold text-gray-900">{t("help.self.q", "Key responsibility: run the monthly payroll accurately and on time")}</p>
        <p className="mb-2 mt-0.5 text-xs text-gray-500">{t("help.self.qHint", "How did this go this year? Rate yourself and add an example.")}</p>
        <RatingScale value={rating} onChange={setRating} scale="self" name={t("help.self.aria", "Demo self-rating")} />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder={t("help.self.placeholder", "For example: every payroll went out on time this year, including December's early run...")}
          className="mt-2 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-pme-blue"
        />
        <p className="mt-1 text-[11px] text-gray-500">
          {t("help.self.note", "Notice the four words — there are no numbers here, so a self-rating is never read as the company 1–5 rating.")}
        </p>
      </TryIt>
    </DemoCard>
  );
}

function PeersStageDemo() {
  const { t } = useTranslation();
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState("");

  return (
    <DemoCard
      title={t("help.peers.title", "Stage 2 — Colleague feedback")}
      intro={t("help.peers.intro", "The manager asks a few colleagues — usually people Sam works with every week and a couple of internal customers. Each gets a short form. The manager reads every answer with names; Sam only ever sees themes, never who said what.")}
    >
      <TryIt>
        <p className="text-sm font-semibold text-gray-900">{t("help.peers.q", "How is it working with Sam day to day?")}</p>
        <p className="mb-2 mt-0.5 text-xs text-gray-500">{t("help.peers.qHint", "This is what a colleague sees. Same four words, plus a comment.")}</p>
        <RatingScale value={rating} onChange={setRating} scale="colleague" name={t("help.peers.aria", "Demo colleague rating")} />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder={t("help.peers.placeholder", "For example: Sam always comes back the same day, even on fiddly questions...")}
          className="mt-2 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-pme-blue"
        />
        <p className="mt-1 text-[11px] text-gray-500">
          {t("help.peers.note", "Colleague answers are anonymous to the person being appraised — a safe space to be honest and kind.")}
        </p>
      </TryIt>
    </DemoCard>
  );
}

function ManagerStageDemo() {
  const { t } = useTranslation();
  const [rating, setRating] = useState<number | null>(null);
  const [example, setExample] = useState("");
  const missing = needsExample(rating) && !hasExample(example);

  return (
    <DemoCard
      title={t("help.mgr.title", "Stage 3 — The manager's review")}
      intro={t("help.mgr.intro", "The manager writes their view of the year with the self-assessment and colleague feedback beside them, and rates each area on the company 1–5 scale. A 3 is a solid job — most good performers sit there.")}
    >
      <TryIt>
        <p className="text-sm font-semibold text-gray-900">{t("help.mgr.q", "Rate Sam on: payroll processing")}</p>
        <p className="mb-2 mt-0.5 text-xs text-gray-500">{t("help.mgr.qHint", "Pick a 4 or 5 and watch what happens.")}</p>
        <RatingScale value={rating} onChange={setRating} name={t("help.mgr.aria", "Demo manager rating")} />
        {needsExample(rating) && (
          <div className={`mt-2 rounded-lg border p-2.5 ${missing ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"}`}>
            <p className={`text-xs font-semibold ${missing ? "text-amber-800" : "text-green-700"}`}>
              {missing
                ? t("help.mgr.exampleNeeded", "A {{n}} needs a specific example of at least {{chars}} characters — the system won't let the review be submitted without one.", { n: rating, chars: MIN_EXAMPLE_CHARS })
                : t("help.mgr.exampleOk", "That's enough — the rating can now be submitted.")}
            </p>
            <textarea
              value={example}
              onChange={(e) => setExample(e.target.value)}
              rows={2}
              placeholder={t("help.mgr.examplePlaceholder", "For example: Sam caught the pension mis-posting in March before payday and saved a re-run...")}
              className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm outline-none focus:border-pme-blue"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              {example.trim().length}/{MIN_EXAMPLE_CHARS} {t("help.mgr.chars", "characters")}
            </p>
          </div>
        )}
        <p className="mt-2 text-[11px] text-gray-500">
          {t("help.mgr.note", "High ratings need evidence. That keeps a 5 meaning the same thing in every team.")}
        </p>
      </TryIt>
    </DemoCard>
  );
}

function MeetingStageDemo() {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState<number | null>(null);
  const [timeframe, setTimeframe] = useState<string | null>(null);
  const frames = [
    { key: "1m", label: t("help.meet.1m", "1 month") },
    { key: "3m", label: t("help.meet.3m", "3 months") },
    { key: "6m", label: t("help.meet.6m", "6 months") },
    { key: "12m", label: t("help.meet.12m", "12 months") },
  ];

  return (
    <DemoCard
      title={t("help.meet.title", "Stage 4 — The review meeting")}
      intro={t("help.meet.intro", "The manager books a meeting and both sides talk the year through, using an agenda built from what they each wrote. Together they agree the final rating, the objectives for the year ahead (each with a time frame), and how often to catch up. Pay is not discussed here — salary is a separate conversation on its own date.")}
    >
      <TryIt>
        <p className="text-sm font-semibold text-gray-900">{t("help.meet.q1", "Agree the final rating together")}</p>
        <div className="mt-1.5">
          <RatingScale value={agreed} onChange={setAgreed} size="lg" name={t("help.meet.aria", "Demo agreed rating")} />
        </div>
        <p className="mt-3 text-sm font-semibold text-gray-900">
          {t("help.meet.q2", "Objective: hand the monthly payroll summary to Finance by working day 3")}
        </p>
        <p className="mb-1.5 mt-0.5 text-xs text-gray-500">{t("help.meet.q2Hint", "Every objective gets a time frame, counted from the meeting.")}</p>
        <div className="flex flex-wrap gap-1.5">
          {frames.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setTimeframe(f.key)}
              aria-pressed={timeframe === f.key}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                timeframe === f.key ? "border-pme-blue bg-pme-blue text-white" : "border-gray-200 bg-white text-gray-600 hover:border-pme-blue/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </TryIt>
    </DemoCard>
  );
}

function OutcomeStageDemo() {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<Record<number, string | null>>({ 0: "on_track", 1: null });
  const objectives = [
    t("help.out.obj1", "Hand the monthly payroll summary to Finance by working day 3"),
    t("help.out.obj2", "Cut payroll corrections to fewer than 2 per run"),
  ];
  const states = [
    { key: "on_track", label: t("help.out.onTrack", "On track"), cls: "border-green-300 bg-green-50 text-green-700" },
    { key: "need_help", label: t("help.out.needHelp", "Need help"), cls: "border-amber-300 bg-amber-50 text-amber-800" },
    { key: "done", label: t("help.out.done", "Done"), cls: "border-pme-blue bg-pme-blue text-white" },
  ];

  return (
    <DemoCard
      title={t("help.out.title", "Stage 5 — The outcome, and the year after")}
      intro={t("help.out.intro", "Once the meeting is finished and the feedback released, the person reads the outcome and can add their own response. Their agreed objectives then live on their Performance page all year, and regular catch-ups keep them moving — the appraisal is the start of the year, not the end of it.")}
    >
      <TryIt>
        <p className="mb-2 text-sm font-semibold text-gray-900">{t("help.out.q", "Sam's objectives for the year — update progress at any time")}</p>
        <ul className="space-y-2">
          {objectives.map((obj, i) => (
            <li key={i} className="rounded-lg border border-gray-200 bg-white p-2.5">
              <p className="text-sm text-gray-800">{obj}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {states.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    aria-pressed={progress[i] === s.key}
                    onClick={() => setProgress((p) => ({ ...p, [i]: p[i] === s.key ? null : s.key }))}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                      progress[i] === s.key ? s.cls : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[11px] text-gray-500">
          {t("help.out.note", "Both the person and their manager can update progress, and each catch-up walks through the list.")}
        </p>
      </TryIt>
    </DemoCard>
  );
}

/* ── Job descriptions ────────────────────────────────────────────────────── */

function JdDemo() {
  const { t } = useTranslation();
  const [read, setRead] = useState(false);
  const [outcome, setOutcome] = useState<"signed" | "change" | null>(null);
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [changeSent, setChangeSent] = useState(false);

  const reset = () => {
    setOutcome(null);
    setRead(false);
    setSignature(null);
    setSignedAt(null);
    setComment("");
    setChangeSent(false);
  };

  return (
    <>
      <section className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700">
        <h2 className="text-base font-bold text-pme-navy">{t("help.jd.howTitle", "How job descriptions work now")}</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>{t("help.jd.how1", "Every job description lives in PME People — written and edited online by the line manager (or HR), not as a Word file.")}</li>
          <li>{t("help.jd.how2", "They all follow the same structure: role details, purpose, weighted key result areas totalling 100%, and behavioural competencies.")}</li>
          <li>{t("help.jd.how3", "When the manager issues it, the person reads it here and signs it here — no printing, no scanning.")}</li>
          <li>{t("help.jd.how4", "A change is a new version; old versions and signatures are kept forever.")}</li>
        </ul>
        <p className="mt-2 text-xs text-gray-500">
          {t("help.jd.howManager", "Managers: open My Team, pick a person, then Job description. Click any part of it to edit, and issue it when it's ready.")}
        </p>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-pme-navy">
          <FileSignature size={18} className="text-pme-blue" aria-hidden />
          {t("help.jd.demoHeading", "Sam Taylor's job description — a demo to click through")}
        </h2>
        <p className="mb-3 mt-1 text-sm text-gray-600">
          {t("help.jd.demoIntro", "This is exactly what a person sees when their manager issues their job description.")}
        </p>
        <JobDescriptionView content={demoJd(t)} />

        <div className="mt-4 rounded-xl border border-pme-blue/30 bg-pme-sky/40 p-3">
          {outcome === null && (
            <>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="mt-0.5 h-4 w-4" />
                <span>{t("help.jd.readTick", "I have read and understood my job description.")}</span>
              </label>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!read}
                  onClick={() => setSigning(true)}
                  className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                >
                  {t("help.jd.signBtn", "Sign it")}
                </button>
                <button
                  type="button"
                  onClick={() => setOutcome("change")}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700"
                >
                  {t("help.jd.changeBtn", "Ask for a change")}
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-gray-500">
                {t("help.jd.demoOnly2", "Signing opens the real signature window — draw, type or upload one. It's a demo: nothing is saved or sent.")}
              </p>
            </>
          )}
          {outcome === "signed" && (
            <div className="text-sm text-gray-800">
              <p className="flex items-center gap-1.5 font-semibold text-green-700">
                <CheckCircle2 size={16} aria-hidden /> {t("help.jd.signedTitle2", "Signed — this is what the record now shows")}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-white p-2.5">
                {signature && (
                  <img src={signature} alt={t("help.jd.sigAlt", "The signature you just made")} className="h-14 max-w-[220px] object-contain" />
                )}
                <div className="text-xs text-gray-700">
                  <p className="font-semibold text-gray-900">Sam Taylor</p>
                  <p>{t("help.jd.signedOn", "Signed on")} {signedAt ? formatDate(signedAt, "long") : ""}</p>
                </div>
              </div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-gray-600">
                <li>{t("help.jd.signed2", "A signed PDF is filed in their documents as the permanent record.")}</li>
                <li>{t("help.jd.signed3", "Their manager is told it's been signed.")}</li>
              </ul>
              <button type="button" onClick={reset} className="mt-2 text-xs font-semibold text-pme-blue hover:underline">
                {t("help.jd.again", "Reset the demo")}
              </button>
            </div>
          )}
          {outcome === "change" && !changeSent && (
            <div className="text-sm text-gray-800">
              <p className="font-semibold text-pme-navy">{t("help.jd.changeTitle", "Asking for a change")}</p>
              <p className="mt-0.5 text-xs text-gray-600">{t("help.jd.changeHint", "Tell your manager what's wrong or out of date, then send it.")}</p>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder={t("help.jd.changePlaceholder", "For example: I no longer run the weekly BACS file — that moved to Finance in June.")}
                className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm outline-none focus:border-pme-blue"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!comment.trim()}
                  onClick={() => setChangeSent(true)}
                  className="rounded-lg bg-pme-navy px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
                >
                  {t("help.jd.changeSend", "Send to your manager (demo)")}
                </button>
                <button type="button" onClick={reset} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-600">
                  {t("help.jd.back", "Back")}
                </button>
              </div>
            </div>
          )}
          {outcome === "change" && changeSent && (
            <div className="text-sm text-gray-800">
              <p className="font-semibold text-amber-800">{t("help.jd.changeSentTitle", "Change asked for — this is what the manager sees")}</p>
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                <p className="font-semibold">{t("help.jd.changeCard", "Sam Taylor asked for a change:")}</p>
                <p className="mt-1 whitespace-pre-wrap">"{comment.trim()}"</p>
              </div>
              <p className="mt-2 text-xs text-gray-600">
                {t("help.jd.change1", "In real life the comment goes to the manager, who edits the job description and issues a new version to sign. Nothing is signed until the person is happy.")}
              </p>
              <button type="button" onClick={reset} className="mt-2 text-xs font-semibold text-pme-blue hover:underline">
                {t("help.jd.again", "Reset the demo")}
              </button>
            </div>
          )}
        </div>
      </section>

      {signing && (
        <SignatureModal
          doc={{ id: 0, title: t("help.jd.sigDocTitle", "Job description (demo)") }}
          onSubmit={async (signatureData) => {
            setSignature(signatureData);
            setSignedAt(new Date().toISOString());
          }}
          onClose={(signed) => {
            setSigning(false);
            if (signed) setOutcome("signed");
          }}
        />
      )}
    </>
  );
}

/* ── The rating scales, side by side ─────────────────────────────────────── */

function ScalesReference() {
  const { t } = useTranslation();
  const company = ratingScale();
  const words = reflectionScale("self");

  return (
    <>
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-base font-bold text-pme-navy">{t("help.scales.companyTitle", "The company 1–5 scale")}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t("help.scales.companyIntro", "Used by the manager, and for the final rating agreed in the meeting. A 3 is a solid job — most good performers sit here. A 4 or 5 always needs a written example.")}
        </p>
        <ul className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200">
          {company.map((l) => (
            <li key={l.value} className="flex items-start gap-3 px-3 py-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-pme-blue text-sm font-bold text-white">{l.value}</span>
              <span>
                <span className="block text-sm font-semibold text-gray-900">{l.label}</span>
                <span className="block text-xs text-gray-600">{l.hint}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-base font-bold text-pme-navy">{t("help.scales.wordsTitle", "The reflection words")}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t("help.scales.wordsIntro", "Used for self-assessment and colleague feedback. Words, not numbers, so a reflection is never mistaken for the company rating.")}
        </p>
        <ul className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200">
          {words.map((l) => (
            <li key={l.value} className="flex items-start gap-3 px-3 py-2">
              <span className="mt-0.5 shrink-0 rounded-full bg-pme-navy px-2.5 py-0.5 text-xs font-bold text-white">{l.label}</span>
              <span className="text-xs text-gray-600">{l.hint}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500">
          <CircleHelp size={14} className="mt-0.5 shrink-0" aria-hidden />
          {t("help.scales.note", "Why two scales? So people reflect honestly without scoring themselves, and the one number that matters — the agreed rating — is reached together in the meeting.")}
        </p>
      </section>
    </>
  );
}
